import { create } from 'zustand';
import { useLive } from '../live/store';
import { ApiError, robotCmd } from '../api/client';
import type { GuardStatus } from '../api/types';
import { toast } from '../components/toast';
import { t } from '../i18n';

/** Arguments of the robot's `guard` command (PROTOCOL §25). `pause`: true = the robot's service pause (15 min), false = resume. */
export interface GuardArgs {
  enabled?: boolean;
  warnDistance?: number;
  pause?: boolean | number;
}

export const GUARD_DISTANCE = { min: 0.5, max: 3.0, step: 0.1, default: 1.5 } as const;

/** A guard command in flight per robot (the panel, the tile switch). */
export const useGuardPending = create<{ pending: Record<string, true>; set: (serial: string, on: boolean) => void }>((set) => ({
  pending: {},
  set: (serial, on) =>
    set((s) => {
      const pending = { ...s.pending };
      if (on) pending[serial] = true;
      else delete pending[serial];
      return { pending };
    }),
}));

/** `status.guard` of a robot (null when its app does not report one) and whether a command is in flight. */
export function useGuard(serial: string): { guard: GuardStatus | null; pending: boolean } {
  const guard = useLive((s) => s.robots[serial]?.status?.guard ?? null);
  const pending = useGuardPending((s) => Boolean(s.pending[serial]));
  return { guard, pending };
}

export const round1 = (v: number) => Math.round(v * 10) / 10;

/**
 * Send `guard {…}` to the robot; its result (the new `status.guard`) is shown at once, the next status confirms it.
 * On failure: a toast in plain words with Retry.
 */
export async function guardCmd(serial: string, args: GuardArgs): Promise<boolean> {
  const live = useLive.getState();
  const pend = useGuardPending.getState();
  if (pend.pending[serial]) return false; // one at a time per robot
  const name = live.robots[serial]?.name ?? serial;
  live.addLog(serial, 'cmd', '→ guard', JSON.stringify(args));
  pend.set(serial, true);
  let why: string | null;
  try {
    const r = await robotCmd<GuardStatus>(serial, 'guard', args as Record<string, unknown>);
    if (r.ok) {
      live.addLog(serial, 'ok', '✓ guard', '');
      const data = (r as { data?: GuardStatus }).data;
      const st = useLive.getState().robots[serial]?.status;
      if (st && data && typeof data === 'object') useLive.getState().setStatus(serial, true, { ...st, guard: { ...(st.guard ?? {}), ...data } });
      return true;
    }
    live.addLog(serial, 'err', '✗ guard', `${r.error.code}: ${r.error.message}`);
    const c = r.error.code.toUpperCase();
    why = c === 'TIMEOUT' || c === 'OFFLINE' ? null : c === 'UNKNOWN_COMMAND' || c === 'BAD_ARGS' && /not available/.test(r.error.message) ? t('guard.whyUnsupported') : r.error.message || r.error.code;
  } catch (e) {
    const status = e instanceof ApiError ? e.status : 0;
    live.addLog(serial, 'err', '✗ guard', (e as Error).message);
    why = status === 504 || status === 503 || status === 0 ? null : status === 404 ? t('guard.whyUnsupported') : (e as Error).message;
  } finally {
    useGuardPending.getState().set(serial, false);
  }
  toast(why === null ? t('guard.failSilent', { name }) : t('guard.fail', { name, why }), 'err', { label: t('hand.retry'), onClick: () => void guardCmd(serial, args) });
  return false;
}
